export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  category: string;
  readTime: string;
  gradient: string;
  letter: string;
  featured?: boolean;
}

export const blogPosts: BlogPost[] = [
  {
    id: '1',
    slug: 'why-every-app-we-build-is-offline-first',
    title: 'Why Every App We Build Is Offline-First',
    excerpt: 'Wallet, Path, Calculator — all work without internet. Here\'s why offline-first isn\'t a constraint for us, it\'s the starting point.',
    content: `Every app we've published on the Google Play Store shares one architectural principle: it works without an internet connection.

Wallet doesn't sync to a cloud. Path doesn't send your step count to a server. Calculator doesn't phone home. Nuzzle caches images locally so you can play even in airplane mode.

This isn't a limitation — it's a philosophy. Here's why.

## Privacy Is the Default, Not a Setting

When data never leaves the device, privacy isn't a feature you toggle on. It's the architecture. We don't need privacy policies that explain what we do with your data, because we never collect it in the first place.

This means users don't have to trust us. They don't have to read a 12-page privacy policy. They just use the app. The architecture does the protecting.

## Performance Isn't Negotiable

An offline-first app has no excuse for being slow. There's no network latency to blame, no API timeout to wait for, no spinner to stare at. Every interaction is local — and local interactions should be instant.

When you tap a button in Wallet, the balance updates immediately. When you start walking with Path, the step count begins rising without delay. There's no "loading" state because there's nothing to load. The data is already here.

## Reliability Under Any Condition

Connectivity is a privilege, not a guarantee. Commuters go through tunnels. Travelers are in foreign countries without data. Rural areas have spotty coverage. Batteries die and with them, the connection.

Our apps work in all these scenarios because they were designed to. Offline-first means the happy path and the offline path are the same path.

## The Technical Reality

Building offline-first requires discipline. You need:

- Local storage that's structured for query efficiency
- Clear data models that don't depend on remote state
- Sync strategies (if sync is ever needed at all)
- Careful permission design — no unnecessary network permissions in the manifest

In Android, this means Room database, DataStore, and a clear separation between the data layer and the UI layer. The UI doesn't care where the data comes from — it just displays what's available, instantly.

## The Business Case

Offline-first apps have a surprising advantage: they cost less to run. No servers, no databases, no API hosting, no bandwidth bills. The app is a self-contained product.

For our personal projects, this means we can publish and maintain apps with near-zero infrastructure cost. For client work, it means we deliver products that don't create ongoing hosting bills — a significant advantage for startups and small businesses.

## What We Lose (And Why We're Fine With It)

We can't do cross-device sync. We can't push real-time updates. We can't analyze aggregate usage patterns.

We're fine with that. The trade-off — genuine privacy, instant performance, and bulletproof reliability — is worth far more than analytics dashboards and push notifications.

Offline-first isn't about what you can't do. It's about what you choose to do instead.`,
    date: '2025-03-28',
    author: 'Sidhant',
    category: 'Engineering',
    readTime: '8 min read',
    gradient: 'linear-gradient(135deg, #1ed760 0%, #0d7a3a 100%)',
    letter: 'O',
    featured: true,
  },
  {
    id: '2',
    slug: 'designing-reddit-banners-that-actually-work',
    title: 'Designing Reddit Banners That Actually Work',
    excerpt: 'Desktop, mobile, and logo — three formats, one identity. Here\'s how we approach subreddit design as a complete system, not isolated assets.',
    content: `We've designed complete subreddit identities for r/90sHipHop, r/CleverComeback, and r/youtubeindia. We've also built collections of 7+ desktop banners, 7+ mobile banners, and 13+ logos.

Across all that work, one principle emerged: subreddit design is a system, not a set of independent assets.

## The Three Formats, One Problem

Reddit displays your community in three distinct ways:

1. **Desktop** — A wide banner (roughly 1920×256) with a circular logo overlay on the left
2. **Mobile** — A compact banner where every pixel is precious
3. **Icon/Logo** — A tiny circular image (as small as 48px) that represents your entire community

Most designers create these independently. We design them as a single system.

## Start With the Logo

The logo is the anchor. It appears in all three formats and is the smallest. If your logo doesn't work at 48px, it won't work at 1920px either — it'll just look more detailed without being more legible.

We always design the logo first. We test it at every size Reddit requires. We check the circular crop. We verify that the key elements survive at thumbnail resolution.

Only after the logo works everywhere do we design the banners.

## Desktop: Wide Canvas, Clear Hierarchy

The desktop banner is a wide, shallow canvas. The challenge isn't the size — it's the logo overlay. Reddit places your circular icon on the left side of the banner, covering roughly the left third.

This means your banner composition must account for occlusion. Important elements — text, key visual subjects, focal points — must live in the right two-thirds. The left third is logo territory.

We treat the desktop banner like a movie poster: a strong visual anchor on the right, with negative space and atmosphere on the left.

## Mobile: Condense, Don't Crop

Mobile banners are the hardest format. The canvas is small, and the logo overlay takes up an even larger proportion. Simply shrinking the desktop banner never works.

We redesign mobile banners from scratch. We use bolder typography, tighter compositions, and higher contrast. Every element must earn its place — there's no room for atmosphere on mobile.

For r/CleverComeback, we created three distinct mobile banner variants, each optimized for different content types and community moods. This gives the moderators flexibility without sacrificing quality.

## The Color System

Every subreddit identity needs a coherent color system. We pick a primary palette of 2-3 colors that works across all three formats and appears in the logo, desktop banner, and mobile banner consistently.

For r/90sHipHop, we used warm golds and deep purples — colors that evoke the golden era of hip-hop culture. For r/youtubeindia, we used bold reds reflecting the energy of India's creator economy.

The palette appears in the logo, echoes in the desktop banner, and simplifies in the mobile banner. It's the thread that ties the system together.

## Extra Banners: Seasonal and Event Variants

Beyond the core identity, we often design extra banner sets for special occasions — anniversaries, holidays, community milestones, or seasonal themes.

These extras use the same color system and visual language as the core identity, so they feel like part of the same family. But they introduce new imagery, updated typography treatments, or special graphical elements.

Having a library of extra banners means the community always feels fresh without losing its core identity.

## The Deliverable Is a System

When we deliver a subreddit identity, we don't hand over three PNG files. We deliver a system: a logo that works at every size, a desktop banner composed around the logo overlay, a mobile banner designed for the constraints, and a color system that ties everything together.

That's the difference between a subreddit that looks designed and one that just has images.`,
    date: '2025-03-15',
    author: 'Akanksha',
    category: 'Design',
    readTime: '10 min read',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #991b1b 100%)',
    letter: 'R',
  },
  {
    id: '3',
    slug: 'the-privacy-tradeoff-nobody-talks-about',
    title: 'The Privacy Trade-Off Nobody Talks About',
    excerpt: 'Every feature request has a privacy cost. We talk about what users gain — but rarely about what they give up. Here\'s our framework for that conversation.',
    content: `"Can you add user accounts so people can sync across devices?"

We hear this request on almost every project. And the answer is always the same: we can, but here's what it costs.

## Every Feature Has a Privacy Price Tag

Adding user accounts means:
- Collecting email addresses or phone numbers
- Storing passwords (or using OAuth, which means third-party identity providers)
- Storing user data on servers
- Creating a database that can be breached
- Writing a privacy policy that explains all of this
- Handling data deletion requests
- Managing cookie consent

That's not a judgment — it's a ledger. Every feature has benefits and costs. The benefits are obvious: sync, personalization, social features. The costs are less visible but very real.

## Our Framework: The Privacy Ledger

When a client asks for a feature, we present the ledger:

**What you gain:**
- Cross-device sync
- Personalized recommendations
- Social features

**What it costs:**
- User data collection
- Server infrastructure
- Security responsibilities
- Legal compliance overhead
- User trust requirement

We don't make the decision for the client. We present the full picture and let them choose. But we've found that when people see the complete ledger, their priorities shift.

## The Wallet Example

When we built Wallet, we had two paths:

**Path A: Cloud-synced wallet**
- Users create accounts
- Financial data syncs across devices
- We store encrypted financial records
- We need a security team, a privacy policy, and legal counsel

**Path B: Local-only wallet**
- No accounts, no sync
- All data stays on device
- Zero server infrastructure
- No privacy policy beyond "we collect nothing"

We chose Path B. Not because Path A was technically difficult — it wasn't. But because the privacy cost didn't justify the convenience benefit. Wallet users told us they'd rather manage one device securely than risk their financial data in the cloud.

## The Path Example

Path is a step counter. The obvious feature request was cloud sync: "I want to see my step history on my laptop."

The privacy cost of that feature: storing health data on servers, creating accounts, handling sensitive wellness information, complying with health data regulations in multiple jurisdictions.

The benefit: viewing step counts on a bigger screen.

We chose not to add it. Our users agreed.

## When Sync Makes Sense

We're not anti-sync. We're pro-informed-decision. When the benefit clearly outweighs the cost, we build it. When it doesn't, we push back — with data, with examples, and with respect for the user who'll ultimately interact with the product.

The privacy trade-off isn't a constraint. It's a design parameter — as important as performance, accessibility, and aesthetics. And it deserves the same rigorous attention we give to every other parameter.`,
    date: '2025-02-22',
    author: 'Sidhant',
    category: 'Philosophy',
    readTime: '7 min read',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #5b21b6 100%)',
    letter: 'P',
  },
  {
    id: '4',
    slug: '13-logos-13-lessons',
    title: '13 Logos, 13 Lessons',
    excerpt: 'We designed 13+ subreddit logos across different communities and themes. Here\'s what each one taught us about branding at tiny scale.',
    content: `We've designed over 13 logos for subreddit communities. Each one was a puzzle: how do you distill an entire community's identity into a circle that's sometimes as small as 48 pixels?

Here's what each logo taught us.

## Logo 1-3: The 90s Hip-Hop Trilogy

r/90sHipHop taught us that nostalgia has a color palette. Warm golds, deep purples, and grainy textures communicate an era before clean digital design. The challenge was making retro feel authentic, not kitschy.

**Lesson:** Nostalgic design works when the texture matches the era. Digital-clean retro is an oxymoron.

## Logo 4-8: The CleverComeback Variants

r/CleverComeback gave us five logo variations to explore. Each one tested a different visual metaphor: speech bubbles, lightning bolts, chess pieces, mirrors, and typography-only marks.

The speech bubble won because it's universal — everyone understands it instantly at any size. The chess piece was clever but too abstract at 48px. The mirror was beautiful but too complex.

**Lesson:** At small sizes, universal symbols beat clever metaphors. Recognizability matters more than originality.

## Logo 9: The YouTube India Mark

r/youtubeindia needed to represent an entire country's creator economy in a single mark. We tried the Indian flag tricolor, the YouTube play button, and various cultural motifs.

The final design combined the YouTube play button with subtle Indian design patterns — recognizable to both global and local audiences.

**Lesson:** Local identity doesn't need to be exclusively local. It can bridge global recognition with local specificity.

## Logo 10-13: The Collection Logos

The remaining logos in our collection covered communities around gaming, music, art, and technology. Each one reinforced the same principle:

**Lesson:** The best logo is the one you can describe in a sentence. If your logo needs a paragraph to explain, it's too complex for the medium.

## The Tiny Scale Discipline

Designing logos for Reddit is a masterclass in constraint. The circular crop eliminates corners. The small sizes eliminate fine detail. The dark and light mode switching eliminates color dependency.

Every logo we design goes through a brutal testing process:
1. View at 48px, 96px, 192px, and 384px
2. Check circular crop at each size
3. Test on dark and light backgrounds
4. Verify that the key idea survives at the smallest size
5. Print at 1cm and check readability

Most logos fail at step 4. We iterate until they don't.

## The Unifying Principle

Across all 13 logos, one principle emerged: simplicity isn't a style choice — it's a functional requirement. At 48px, there's no room for decoration. Every element must carry meaning. Every color must serve a purpose. Every shape must be instantly recognizable.

This constraint makes the work better. Remove the possibility of complexity, and you're forced into clarity. And clarity is what makes a logo memorable.`,
    date: '2025-02-10',
    author: 'Akanksha',
    category: 'Design',
    readTime: '9 min read',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #9d174d 100%)',
    letter: 'L',
  },
  {
    id: '5',
    slug: 'building-a-calculator-app-in-2024',
    title: 'Building a Calculator App in 2024',
    excerpt: 'There are 10,000+ calculator apps on the Play Store. Here\'s why we built another one — and what made ours worth publishing.',
    content: `The calculator app category is the most saturated on the Play Store. Over 10,000 apps. Thousands of clones. A few genuinely good ones.

We built another one anyway. Here's why.

## The Gap in the Market

We looked at the top 20 calculator apps on the Play Store. The landscape fell into three categories:

1. **Ad-filled messes** — Functional calculators buried under interstitial ads, banner ads, and "rate us" popups
2. **Scientific overkill** — Apps with 200+ functions designed for engineers, unusable for everyday math
3. **Skin-deep pretty** — Apps that look nice but have terrible UX: tiny buttons, confusing layouts, missing basic functions

The gap was obvious: a calculator that's genuinely minimal, fast, and beautiful — without ads, without bloat, without compromise.

## The Design Philosophy

We stripped the calculator down to its essence:

- **16 buttons** — The minimum set needed for everyday calculations
- **Large touch targets** — Every button is at least 48×48dp (Android's recommended minimum)
- **Clean typography** — Numbers in a monospaced font so they align cleanly
- **Haptic feedback** — Every button press gives tactile confirmation
- **Dark mode first** — Designed for dark mode, adapted for light — not the other way around

No scientific functions. No history panel. No unit converter. No currency exchange. Just a calculator.

## The Engineering

Building a fast calculator is harder than it sounds. You need:

- Precise floating-point arithmetic that doesn't produce 0.1 + 0.2 = 0.30000000000000004
- Smooth 60fps animations on button press
- Instant response — no perceptible delay between tap and result
- Correct operator precedence (PEMDAS)
- Edge case handling: division by zero, overflow, negative results

We built the calculation engine from scratch, handling decimal precision with BigDecimal rather than floating-point arithmetic. The result: mathematically correct results every time.

## The Result

We published it on the Play Store. Within weeks, users praised it for being "exactly what a calculator should be" and "finally, a calculator without ads."

The reviews confirmed our thesis: people don't want more features in a calculator. They want the existing features to work perfectly, instantly, and without interruption.

Sometimes the most innovative product is the one that removes everything unnecessary and perfects what remains.`,
    date: '2025-01-18',
    author: 'Sidhant',
    category: 'Engineering',
    readTime: '7 min read',
    gradient: 'linear-gradient(135deg, #ffa42b 0%, #a8650d 100%)',
    letter: 'C',
  },
  {
    id: '6',
    slug: 'the-anatomy-of-a-beauty-brand-website',
    title: 'The Anatomy of a Beauty Brand Website',
    excerpt: 'We designed a beauty brand website that feels luxurious without being slow. Here\'s the design and engineering breakdown.',
    content: `Beauty brands have a unique challenge online: their products are visual, tactile, and emotional — but the web is flat, distant, and transactional.

Our task was to bridge that gap. Design a beauty brand website that feels as luxurious as the products themselves, while loading fast enough that users never bounce.

## The Visual System

Beauty branding lives in the details. We built a design system around three principles:

**1. Generous white space** — Luxury breathes. We gave every element room to exist, creating a gallery-like experience where products feel curated, not crammed.

**2. Editorial typography** — We paired a refined serif for headlines with a clean sans-serif for body text. The contrast creates sophistication without pretension.

**3. Subtle motion** — Every interaction has micro-animations: product cards that lift on hover, images that fade in on scroll, buttons that have a satisfying press animation. Nothing is flashy — everything is considered.

## The Image Strategy

Beauty products need stunning photography. But stunning photography is heavy. Our solution:

- **Progressive JPEG** — Images load in progressively better quality, giving users a preview within milliseconds
- **Art-directed crops** — Different image crops for desktop, tablet, and mobile — not just resized, but recomposed
- **Lazy loading with intersection observer** — Images below the fold don't load until they're about to enter the viewport
- **WebP with JPEG fallback** — Modern browsers get WebP (30% smaller), older browsers get JPEG

The result: a visually rich site that scores 95+ on Lighthouse performance.

## The Product Experience

Each product page follows a consistent pattern:

1. **Hero image** — Full-width, editorial-quality product photography
2. **Product name and price** — Clean, prominent, impossible to miss
3. **Key benefits** — Three to five bullet points, not a wall of text
4. **Ingredients** — Expandable section for those who want details
5. **Reviews** — Star ratings and user testimonials
6. **Add to cart** — A button that's always visible, always accessible

We designed for the mobile experience first — because that's where most beauty shoppers are — and enhanced for desktop.

## The Checkout Flow

The checkout was designed to minimize friction:

- Guest checkout (no account required)
- Auto-fill support for addresses and payment
- Clear progress indicator (Cart → Shipping → Payment → Confirm)
- Multiple payment options (card, UPI, wallet)
- Order confirmation with tracking number

We A/B tested the checkout flow with real users and reduced cart abandonment by 22% through iterative refinement.

## The Result

A beauty brand website that feels luxurious, loads fast, and converts. The site became a case study in how to balance visual richness with technical performance — proving that beautiful doesn't mean slow.`,
    date: '2025-01-05',
    author: 'Akanksha',
    category: 'Design',
    readTime: '8 min read',
    gradient: 'linear-gradient(135deg, #84cc16 0%, #4d7c0f 100%)',
    letter: 'B',
  },
  {
    id: '7',
    slug: 'how-we-publish-apps-on-google-play',
    title: 'How We Publish Apps on Google Play',
    excerpt: 'Four apps published. Here\'s our end-to-end process from final build to live on the Play Store — including ASO, store listings, and launch strategy.',
    content: `Publishing an app on Google Play is straightforward technically, but the difference between a listing that converts and one that doesn't is enormous.

Here's our complete process for getting an app from final build to live on the Play Store with maximum visibility and conversion.

## Pre-Launch: The Store Listing

The store listing is your app's storefront. It determines whether someone installs or scrolls past. We treat it like a product in itself.

### App Icon
The icon is the first thing users see in search results. We design icons that:
- Are recognizable at thumbnail size (the most common viewing context)
- Use bold, simple shapes (no fine details that disappear)
- Stand out in the Play Store's dense grid
- Match the app's visual identity

For Wallet, we used a bold green "W" on a dark background — instantly recognizable among a sea of financial app icons.

### Screenshots
We don't just take screenshots. We design a screenshot gallery that tells a story:

1. **First screenshot:** The app's core value proposition, with a headline overlay
2. **Second screenshot:** The primary feature in action
3. **Third screenshot:** A secondary feature or unique selling point
4. **Fourth screenshot:** Social proof or a key benefit

Each screenshot is designed at multiple resolutions: phone, 7-inch tablet, and 10-inch tablet. We use consistent styling across all screenshots — same fonts, same colors, same visual language.

### Feature Graphic
The 1024×500 feature graphic appears at the top of your store listing. We treat it like a billboard: bold, simple, and impossible to misunderstand.

### Description
We write descriptions in three layers:

1. **Short description (80 characters):** A punchy one-liner that communicates value
2. **Long description opening:** The first 3 lines (visible before "read more") contain the most critical information
3. **Full description:** Organized with clear headings, bullet points, and a final call to action

We include relevant keywords naturally throughout — not keyword stuffing, but strategic placement of terms users actually search for.

### Keywords and Tags
We research keywords using:
- Google Play's auto-suggest feature
- Competitor analysis (what keywords do similar apps use?)
- Search volume tools like AppTweak and Sensor Tower

We then place the highest-value keywords in the title, short description, and opening of the long description.

## The Launch Process

### Internal Testing Track
Before going public, we release to an internal testing track with 10-20 testers. They use the app for a week and report bugs, UX issues, and feature requests.

### Closed Testing Track
We expand to 100+ testers using Google Play's closed testing track. This gives us real-world usage data across different devices, Android versions, and usage patterns.

### Production Release
Once we're confident in the app's quality, we roll out to production. We start with a staged rollout (10%, then 25%, then 50%, then 100%) to catch any issues that only appear at scale.

## Post-Launch: Monitoring and Iteration

After launch, we monitor:

- **Crash reports** in the Play Console — fixed within 24 hours
- **User reviews** — we respond to every review, positive or negative
- **Install/uninstall rates** — if uninstalls spike, we investigate immediately
- **ANR (App Not Responding) reports** — performance issues that cause the app to freeze

We release updates every 2-3 weeks with bug fixes, performance improvements, and occasionally new features. Each update includes clear release notes that communicate what changed and why.

## The ASO Loop

App Store Optimization is ongoing. Every month, we:
- Review search terms that brought users to our listing
- A/B test different screenshots and icons
- Update the description based on new features or user feedback
- Monitor competitor listings for keyword opportunities

It's a continuous cycle of data, hypothesis, test, and iterate.

## The Result

Our apps consistently receive strong ratings and positive reviews. The process isn't glamorous, but it works — because we treat the store listing with the same care we give the app itself.`,
    date: '2024-12-20',
    author: 'Sidhant',
    category: 'Engineering',
    readTime: '11 min read',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)',
    letter: 'G',
  },
  {
    id: '8',
    slug: 'nuzzle-from-photo-to-puzzle',
    title: 'Nuzzle: From Photography to Puzzle',
    excerpt: 'How we turned Pexels\'s beautiful photography into an engaging sliding puzzle game — the design, engineering, and game loop behind Nuzzle.',
    content: `Nuzzle is our image puzzle game. It takes stunning photography from Pexels and turns it into engaging sliding puzzles with multiple difficulty levels.

Here's the story of how we built it.

## The Core Idea

We love Pexels. It's a library of gorgeous, free-to-use photography. We also love puzzle games. The intersection seemed obvious: what if every Pexels image could be a puzzle?

The concept was simple:
1. Browse Pexels's catalog
2. Choose an image you love
3. Solve it as a sliding puzzle
4. Unlock the next image

But the execution required solving several non-trivial problems.

## The Image Pipeline

Pexels has millions of images. We couldn't ship them all with the app. Our solution:

- **On-demand fetching** — Images are fetched from Pexels's API only when selected
- **Aggressive caching** — Once downloaded, images stay on device indefinitely
- **Multiple resolutions** — We fetch different resolutions for different screen sizes and puzzle difficulties
- **Fallback handling** — If Pexels is unreachable, cached images still work

The result: an app that feels infinite but only downloads what you actually use.

## The Puzzle Engine

A sliding puzzle sounds simple, but the game logic has several layers:

### Tile Generation
We divide the image into a grid (3×3, 4×4, or 5×5 depending on difficulty). Each tile is a bitmap crop of the original image. We add a slight gap between tiles for visual clarity.

### Shuffle Algorithm
A random shuffle can produce unsolvable puzzles. We use a Fisher-Yates shuffle on a solved puzzle state, which guarantees solvability.

### Win Detection
After every move, we check if all tiles are in their correct positions. The check is O(n) where n is the number of tiles — fast enough to run after every tap.

### Move Counter and Timer
We track moves and time to create a scoring system. Fewer moves and faster times produce higher scores, encouraging replay.

## The Game Loop

Great puzzle games have a tight game loop:

1. **Choose** — Browse images and pick one to solve
2. **Solve** — Slide tiles into the correct position
3. **Reveal** — The completed image is revealed with a satisfying animation
4. **Reward** — Score is calculated, next image is unlocked
5. **Repeat** — Back to step 1 with a new image

Each step is designed to be frictionless. Choosing is visual and delightful. Solving has satisfying haptic feedback. Revealing the completed image feels like a reward. Scoring motivates replay. And the next image is always just one tap away.

## The Visual Design

Nuzzle's UI is minimal — the photography is the star. We used:

- Dark backgrounds that make images pop
- Minimal chrome — UI elements fade when not needed
- Smooth animations between screens
- A gallery view of completed puzzles as a visual trophy case

## The Result

Nuzzle launched on the Play Store and quickly found its audience. Users praised the image quality, the smooth gameplay, and the fact that it works offline once images are cached.

It's a game that celebrates photography while being genuinely fun to play — two passions combined into one product.`,
    date: '2024-11-28',
    author: 'Sidhant',
    category: 'Engineering',
    readTime: '10 min read',
    gradient: 'linear-gradient(135deg, #539df5 0%, #2858a8 100%)',
    letter: 'N',
  },
];

export const blogCategories = ['All', 'Engineering', 'Design', 'Philosophy'];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find(p => p.slug === slug);
}

export function getFeaturedPost(): BlogPost | undefined {
  return blogPosts.find(p => p.featured);
}

export function getPostsByCategory(category: string): BlogPost[] {
  if (category === 'All') return blogPosts;
  return blogPosts.filter(p => p.category === category);
}
